import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Search, X } from 'lucide-react';
import { deviceDisplayName } from '../../utils/deviceName';

// Searchable, A–Z sorted device selector (same pattern as Analytics > History device picker)
export const DevicePicker = ({ devices, value, onChange, allLabel = null, placeholder = 'Select device', disabled = false, className = '', testId = 'device-picker' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const sorted = useMemo(
    () => [...(devices || [])].sort((a, b) => (deviceDisplayName(a) || '').localeCompare(deviceDisplayName(b) || '', undefined, { sensitivity: 'base', numeric: true })),
    [devices],
  );
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return sorted;
    return sorted.filter(d => (deviceDisplayName(d) || '').toLowerCase().includes(s));
  }, [sorted, q]);
  const selected = value && value !== 'all' ? sorted.find(d => d.id === value) : null;
  const label = selected ? deviceDisplayName(selected) : (allLabel && (!value || value === 'all') ? allLabel : null);
  const pick = (v) => { onChange(v); setOpen(false); setQ(''); };
  return (
    <Popover modal={true} open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled} className={`justify-between font-normal dark:bg-[#0D1425] ${className}`} data-testid={testId}>
          <span className="truncate">{label || <span className="text-muted-foreground">{placeholder}</span>}</span>
          <Search className="w-3.5 h-3.5 opacity-50 ml-2 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 z-[10000]" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search by asset name / unit number..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-8 pl-8 pr-8 text-base md:text-sm"
              autoFocus
              data-testid={`${testId}-search`}
            />
            {q && (
              <button onClick={() => setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid={`${testId}-search-clear`}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="h-[220px] overflow-y-auto overscroll-contain" data-testid={`${testId}-list`}>
          {allLabel && (
            <button
              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${(!value || value === 'all') ? 'bg-[#2563eb]/10 text-[#2563eb] font-medium' : ''}`}
              onClick={() => pick('all')}
              data-testid={`${testId}-option-all`}
            >
              {allLabel}
            </button>
          )}
          {filtered.length === 0 && <div className="p-4 text-center text-sm text-muted-foreground">No devices found</div>}
          {filtered.map(d => (
            <button
              key={d.id}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${value === d.id ? 'bg-[#2563eb]/10 text-[#2563eb] font-medium' : ''}`}
              onClick={() => pick(d.id)}
              data-testid={`${testId}-option-${d.id}`}
            >
              {deviceDisplayName(d)}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
